import { saveAs } from 'file-saver';
import { selectTokenAuth } from 'src/redux/auth/auth.selectors';
import { useSelector } from 'react-redux';
import { useState } from 'react';

type TError = string | null;
type TDownloadSimResults = {
  type: 'simulation-results';
  resultsUuid: string | undefined;
  startDate: string; // eg: 2021-07-06T00:00
  endDate: string; // eg: 2021-07-10T00:00
};
type TDownloadUserProfile = {
  type: 'user-profile';
  username: string | undefined; // eg: "admin-canary@gridsingularity.com"
  isLibrary: boolean;
};
type TDownloadUploadedFileGetByUuid = {
  type: 'uploaded-file';
  fileUuid: string | undefined;
  configUuid: string | undefined;
  isLibrary: boolean;
  filename?: string;
};
type TDownloadUploadedFileGetByName = {
  type: 'uploaded-file';
  filename: string | undefined;
  configUuid: string | undefined;
  isLibrary: boolean;
};
type TDownloadApiSummary = {
  type: 'api-summary';
  configUuid: string | undefined;
};
type TUseDownloadAPIProps =
  | TDownloadSimResults
  | TDownloadUserProfile
  | TDownloadUploadedFileGetByUuid
  | TDownloadUploadedFileGetByName
  | TDownloadApiSummary;

const DEFAULT_MESSAGE = `An error occurred while downloading this file.`;

export default function useDownloadAPI(props: TUseDownloadAPIProps): {
  handleDownload(): void;
  loading: boolean;
  error: TError;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<TError>(null);
  const tokenAuth = useSelector(selectTokenAuth);
  const token = tokenAuth?.token;

  function getDownloadVars(): { url: string; filename: string } | null {
    const BASE = process.env.REACT_APP_D3A_WEB_API_URL;

    switch (props.type) {
      case 'simulation-results': {
        if (!props.resultsUuid) return null;

        return {
          url: `${BASE}downloads/download-simulation-results-zip/${props.resultsUuid}/${props.startDate}/${props.endDate}`, // prettier-ignore
          filename: `simulation-${props.resultsUuid}.zip`,
        };
      }

      case 'user-profile': {
        if (!props.username) return null;

        return {
          url: `${BASE}downloads/download-${props.isLibrary ? 'lib' : 'config'}-uploaded-files-zip/${props.username}`, // prettier-ignore
          filename: `${props.username}${props.isLibrary ? 'library' : ''}-archive.zip`,
        };
      }

      case 'uploaded-file': {
        if ('fileUuid' in props) {
          if (!props.fileUuid || !props.configUuid) return null;

          return {
            url:`${BASE}downloads/download-${props.isLibrary ? 'lib' : 'config'}-uploaded-file/${props.configUuid}/${props.fileUuid}`, // prettier-ignore
            filename: props.filename || props.fileUuid,
          };
        } else if (props.filename) {
          if (!props.configUuid) return null;

          return {
            // FIXME: This endpoint is broken? Be aware.
            url: `${BASE}downloads/download-${props.isLibrary ? 'lib' : 'config'}-uploaded-file/${props.configUuid}/${props.filename}`, // prettier-ignore
            filename: props.filename,
          };
        }

        return null;
      }

      case 'api-summary': {
        if (!props.configUuid) return null;

        return {
          url: `${BASE}downloads/download-user-configuration-registration-overview/${props.configUuid}`, // prettier-ignore
          filename: `api-summary-${props.configUuid}.json`,
        };
      }

      default:
        return null;
    }
  }

  async function handleDownload() {
    if (!token) {
      return console.error('User must be logged in!');
    }

    const downloadVars = getDownloadVars();

    if (!downloadVars) return;

    const headers = new Headers();
    headers.append('Authorization', `JWT ${token}`);

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(downloadVars.url, { headers });
      const jsonBody = await response.json();

      if (response.status !== 200) {
        throw jsonBody;
      }

      if (jsonBody?.download_url) {
        window.open(jsonBody.download_url, '_blank');
      } else {
        const blobA = await response.blob();
        const blobB = new Blob([blobA.slice(0, blobA.size)], {
          type: 'text/csv;charset=UTF-8',
        });

        saveAs(blobB, downloadVars.filename);
      }
    } catch (error) {
      // setError(error?.message ?? DEFAULT_MESSAGE);
    } finally {
      setLoading(false);
    }
  }

  return { handleDownload, error, loading };
}
